import { DEFAULT_PREFERENCES, effectiveTheme, THEMES, type ThemeId, type UiPreferences } from "./themes";

/** localStorage key holding a copy of the last applied preferences, read by
 * the inline script in index.html before React boots. */
export const STORAGE_KEY = "bankom.ui";

export type ThemeEnv = { systemDark: boolean; systemReducedMotion: boolean };

/** The only place that writes the theme attributes on <html>. */
export function applyPreferences(
  p: UiPreferences,
  env: ThemeEnv,
  root: HTMLElement = document.documentElement,
): ThemeId {
  const theme = effectiveTheme(p, env.systemDark);
  root.dataset.theme = theme;
  root.dataset.density = p.density;
  root.dataset.text = p.textSize;
  const reduced = p.reducedMotion === "on" || (p.reducedMotion === "system" && env.systemReducedMotion);
  root.dataset.motion = reduced ? "reduced" : "full";
  return theme;
}

export function readEnv(): ThemeEnv {
  if (typeof window === "undefined" || !window.matchMedia) return { systemDark: false, systemReducedMotion: false };
  return {
    systemDark: window.matchMedia("(prefers-color-scheme: dark)").matches,
    systemReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  };
}

export function storePreferences(p: UiPreferences): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* storage unavailable */ }
}

function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && Object.hasOwn(THEMES, value);
}

export function sanitizePreferences(raw: unknown): UiPreferences | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const obj = raw as Record<string, unknown>;
  const result = { ...DEFAULT_PREFERENCES };

  if (isThemeId(obj.theme)) {
    result.theme = obj.theme;
  }

  if (typeof obj.followSystem === "boolean") {
    result.followSystem = obj.followSystem;
  }

  if (isThemeId(obj.lightTheme) && !THEMES[obj.lightTheme].dark) {
    result.lightTheme = obj.lightTheme;
  }

  if (isThemeId(obj.darkTheme) && THEMES[obj.darkTheme].dark) {
    result.darkTheme = obj.darkTheme;
  }

  if (typeof obj.density === "string" && ["compact", "default", "comfortable"].includes(obj.density)) {
    result.density = obj.density as "compact" | "default" | "comfortable";
  }

  if (typeof obj.textSize === "string" && ["small", "medium", "large"].includes(obj.textSize)) {
    result.textSize = obj.textSize as "small" | "medium" | "large";
  }

  if (typeof obj.reducedMotion === "string" && ["system", "on", "off"].includes(obj.reducedMotion)) {
    result.reducedMotion = obj.reducedMotion as "system" | "on" | "off";
  }

  return result;
}

export function loadStoredPreferences(): UiPreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? sanitizePreferences(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/** What the portal shows before anyone signs in: the stored copy, or — on a
 * first visit — the Bankom/Noć pair after the operating system's preference,
 * the same choice the inline script in index.html makes. */
export function initialPreferences(): UiPreferences {
  return loadStoredPreferences() ?? { ...DEFAULT_PREFERENCES, followSystem: true };
}
